/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { planIconFor, planIcons } from "@/shared/ui";
import { AdministrationService } from "@services/requests";
import i18n from '@shared/i18n';

export type PlanFeatureItem = {
  key: string;
  tier: string;
};

export type PlanFeaturesV3 = Record<string, boolean>;
export type PlanLimitsV3 = Record<string, number | null>;

/**
 * A plan's numbers, split the way the catalogue states them.
 *
 * The scope is what decides the arithmetic: an allowance in `seat` is what one
 * person draws down, so an agency's total is that times its seats, while a
 * pool in `agency` is one figure the whole account shares and never
 * multiplies. Storage is the only shared pool today. Toggles are not split
 * here because a plan card asks whether the plan *has* a feature, not who
 * owns it.
 */
export type PlanScopedLimits = {
  agency: PlanLimitsV3;
  seat: PlanLimitsV3;
};
export type PlanOptionsV3 = Record<string, string>;

export type PlanSegment = 'solo' | 'agency';

export type PlanFeatureType = {
  key: string;
  value: string;
  /**
   * The catalogue's own id for this variant.
   *
   * This is what a subscription line carries: the server resolves a plan by
   * id or slug and stores the id, so a screen reading the subscription back
   * has a UUID in hand and nothing else to match it against.
   */
  catalogueId?: string;
  icon: React.FunctionComponent;
  title: string;
  price: number;
  discount: number;
  features: PlanFeaturesV3;
  limits: PlanScopedLimits;
  options: PlanOptionsV3;
  billing_frequency?: string;
  originalId?: string;
  recommended?: boolean;
  description?: string;
  users?: number;
  /** Price of one seat, when the catalogue states it outright. */
  pricePerSeat?: number;
  minSeats?: number;
  segment?: PlanSegment;
  /**
   * Free days before the first charge, as the catalogue sets them.
   *
   * The offer is the catalogue's to make: Payien resolves the trial from its
   * own copy of this same field and bills accordingly, so a screen that
   * decided on its own would promise one thing and charge another.
   */
  trialDays?: number;
  availableSpots?: number;
  availability?: string;
  active?: boolean;
  sortOrder?: number;
};

export const plans_discounts = {
  monthly: 0,
  yearly: 30,
};
export const paymentPeriod = "monthly";

export const plans_icons = planIcons;

export type Plans = {
  plans_features: PlanFeatureType[];
};

export type LocalizedText = {
  pt_br?: string;
  en?: string;
  es?: string;
};

type PlanResponse = {
  id: string;
  slug: string;
  segment?: PlanSegment;
  display_name: LocalizedText;
  description: LocalizedText;
  price_cents: number;
  /** Price of one seat. Absent on the package-priced catalogue. */
  price_per_seat_cents?: number;
  currency: string;
  billing_frequency: string;
  discount_percent?: number;
  /** Free days before the first charge. Absent = charged from day zero. */
  trial_days?: number;
  min_seats?: number;
  max_seats: number;
  available_spots: number;
  /** Housien-only display state: `coming_soon` or `sold_out`. Absent = on sale. */
  availability?: string;
  recommended?: boolean;
  active?: boolean;
  sort_order?: number;
  features?: PlanFeaturesV3 | PlanFeatureItem[];
  limits?: PlanLimitsV3;
  agency_features?: PlanFeaturesV3;
  seat_features?: PlanFeaturesV3;
  agency_limits?: PlanLimitsV3;
  seat_limits?: PlanLimitsV3;
  options?: PlanOptionsV3;
};

/**
 * Strips environment, billing-frequency, and segment suffixes from a plan slug
 * to obtain its base plan identifier.
 *
 * @param slug - Full plan slug.
 * @returns The base plan id.
 */
export function getBasePlanId(slug: string): string {
  return slug
    .replace(/-housien$/, '')
    .replace(/-(?:monthly|yearly)$/, '')
    .replace(/-(?:solo|agency)$/, '');
}

/**
 * Resolves the icon component for a plan from its base id, defaulting to the
 * emerald icon when no match is found.
 *
 * @param plan - Plan response.
 * @returns The icon component for the plan tier.
 */
function getPlanIcon(plan: PlanResponse): React.FunctionComponent {
  return planIconFor(getBasePlanId(plan.slug));
}

const LOCALE_FIELD_MAP: Record<string, keyof LocalizedText> = {
  es: 'es',
  en: 'en',
  pt: 'pt_br',
};

/**
 * Maps a language tag to the matching localized-text field, defaulting to
 * Portuguese when no prefix matches.
 *
 * @param lang - Language tag (e.g. `en-US`).
 * @returns The localized-text field key.
 */
function getLocaleField(lang: string): keyof LocalizedText {
  const prefix = Object.keys(LOCALE_FIELD_MAP).find((k) => lang.startsWith(k));
  return prefix ? LOCALE_FIELD_MAP[prefix] : 'pt_br';
}

/**
 * Returns the first non-empty localized value, checking Portuguese, English,
 * then Spanish.
 *
 * @param text - Localized text object.
 * @returns The first available value, or empty string.
 */
function firstLocalizedValue(text: LocalizedText): string {
  return text.pt_br || text.en || text.es || '';
}

/**
 * Resolves a localized string for the active i18n language, falling back to the
 * first available value.
 *
 * @param text - Localized text object.
 * @returns The resolved localized string.
 */
function resolveLocalized(text: LocalizedText): string {
  const lang = i18n.language || 'pt-BR';
  const field = getLocaleField(lang);
  return text[field] || firstLocalizedValue(text);
}

/**
 * Resolves the localized description for a plan.
 *
 * @param plan - Plan response.
 * @returns The localized description.
 */
function getPlanDescription(plan: PlanResponse): string {
  return resolveLocalized(plan.description || {});
}

/**
 * Resolves the localized display title for a plan.
 *
 * @param plan - Plan response.
 * @returns The localized title.
 */
function getPlanTitle(plan: PlanResponse): string {
  return resolveLocalized(plan.display_name || {});
}

/**
 * Derives the grouping key used to combine plan variants (the base plan id).
 *
 * @param slug - Full plan slug.
 * @returns The combo key.
 */
function getComboKey(slug: string): string {
  return getBasePlanId(slug);
}

/**
 * Extracts the V3 features/limits/options triple from a plan response, returning
 * empty maps when the response is not in the expected V3 shape.
 *
 * @param plan - Plan response.
 * @returns The features, limits, and options maps.
 */
function normalizeFeaturesFromResponse(
  plan: PlanResponse,
): { features: PlanFeaturesV3; limits: PlanScopedLimits; options: PlanOptionsV3 } {
  const options = plan.options ?? {};
  if (plan.agency_features || plan.seat_features) {
    return {
      features: { ...plan.agency_features, ...plan.seat_features },
      limits: { agency: plan.agency_limits ?? {}, seat: plan.seat_limits ?? {} },
      options,
    };
  }
  // A catalogue still published in v3 states one flat map of each. Split it by
  // the same rule the backend uses, so a bucket that has not been migrated
  // yet renders the same as one that has.
  if (plan.limits && !Array.isArray(plan.features)) {
    const { storage_gb: storage, ...seat } = plan.limits;
    return {
      features: (plan.features as PlanFeaturesV3) ?? {},
      limits: {
        agency: storage == null ? {} : { storage_gb: storage },
        seat,
      },
      options,
    };
  }
  return { features: {}, limits: { agency: {}, seat: {} }, options };
}

/**
 * Transforms a raw plan API response into the UI-facing plan feature model,
 * resolving icons, localized copy, pricing, and discounts.
 *
 * Exported so the catalogue's shape can be asserted without standing up the
 * administration service: the reader recognising the payload is the whole
 * reason a shape change once blanked every plan card in silence.
 *
 * @param plan - Plan response.
 * @returns The UI plan feature model.
 */
export function transformPlanToFeature(plan: PlanResponse): PlanFeatureType {
  const comboKey = getComboKey(plan.slug);
  const freq = plan.billing_frequency as keyof typeof plans_discounts;
  const discount = plan.discount_percent ?? plans_discounts[freq] ?? 0;
  const { features, limits, options } = normalizeFeaturesFromResponse(plan);

  return {
    key: comboKey,
    value: plan.slug,
    catalogueId: plan.id,
    icon: getPlanIcon(plan),
    title: getPlanTitle(plan),
    price: plan.price_cents / 100,
    discount,
    billing_frequency: plan.billing_frequency,
    originalId: plan.slug,
    recommended: plan.recommended ?? false,
    description: getPlanDescription(plan),
    features,
    limits,
    options,
    users: plan.max_seats,
    pricePerSeat:
      plan.price_per_seat_cents == null ? undefined : plan.price_per_seat_cents / 100,
    minSeats: plan.min_seats,
    trialDays: plan.trial_days ?? 0,
    segment: plan.segment,
    availableSpots: plan.available_spots,
    availability: plan.availability,
    active: plan.active,
    sortOrder: plan.sort_order,
  };
}

/**
 * Fetches subscription plans from the administration API and maps them into the
 * UI-facing plan feature list.
 *
 * @returns The plans wrapper with transformed plan features.
 */
export const get_plans = async (): Promise<Plans> => {
  const plans_response: PlanResponse[] =
    await AdministrationService.getSubscriptionPlans() as any;

  const plans_features = plans_response.map(transformPlanToFeature);

  return { plans_features };
};
